import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DataProvider } from './context/DataContext';
import { InvoicesProvider } from './context/InvoicesContext';
import { PitchesProvider } from './context/PitchesContext';
import { BrandPitchesProvider } from './context/BrandPitchesContext';
import { RevenueViewProvider } from './context/RevenueViewContext';
import { EmojiProvider } from './context/EmojiContext';
import { Toaster } from './components/ui/toaster';
import { Toaster as SonnerToaster } from './components/ui/sonner';
import GlobalEmojiInserter from './components/GlobalEmojiInserter';

import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import AppShell from './components/AppShell';
import Deals from './pages/Deals';
import Talent from './pages/Talent';
import TalentProfile from './pages/TalentProfile';
import Brands from './pages/Brands';
import BrandDetail from './pages/BrandDetail';
import Calendar from './pages/Calendar';
import Clients from './pages/Clients';
import Invoices from './pages/Invoices';
import InvoiceEditor from './pages/InvoiceEditor';
import Pitching from './pages/Pitching';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Social from './pages/Social';
import PrEvents from './pages/PrEvents';
import Projects from './pages/Projects';
import Expenses from './pages/Expenses';
import Finances from './pages/Finances';
import Insights from './pages/Insights';
import ContentData from './pages/ContentData';
import PitchTemplates from './pages/PitchTemplates';
import Database from './pages/Database';
import Todos from './pages/Todos';
import Mailers from './pages/Mailers';
import MailerDetail from './pages/MailerDetail';
import PublicInsights from './pages/PublicInsights';
import PublicSocialFeed from './pages/PublicSocialFeed';
import PublicMailerReport from './pages/PublicMailerReport';
import PublicContentReport from './pages/PublicContentReport';

const ManagerOnly = ({ children }) => {
  const { user } = useAuth();
  if (user && !['manager', 'talent_manager'].includes(user.role)) return <Navigate to={user.role === 'social_manager' ? '/social' : '/deals'} replace />;
  return children;
};

// Manager only (no talent_manager). Used for clients / finances which
// talent_manager is explicitly restricted from.
const StrictManagerOnly = ({ children }) => {
  const { user } = useAuth();
  if (user && user.role !== 'manager') return <Navigate to={user.role === 'social_manager' ? '/social' : '/deals'} replace />;
  return children;
};

const SocialAccess = ({ children }) => {
  const { user } = useAuth();
  if (user && !['manager', 'social_manager'].includes(user.role) && !user.social_access) return <Navigate to="/deals" replace />;
  return children;
};

// Blocks social_manager (who should only see /social, not /clients) from other pages.
const NotSocialManager = ({ children }) => {
  const { user } = useAuth();
  if (user && user.role === 'social_manager') return <Navigate to="/social" replace />;
  return children;
};

const HomeRedirect = () => {
  const { user } = useAuth();
  if (user?.role === 'social_manager') return <Navigate to="/social" replace />;
  return <Navigate to="/deals" replace />;
};

function AppRouter() {
  const location = useLocation();
  // Detect session_id fragment synchronously (prevents race conditions)
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/share/insights/:token" element={<PublicInsights />} />
      <Route path="/share/social/:token" element={<PublicSocialFeed />} />
      <Route path="/share/mailers/:token" element={<PublicMailerReport />} />
      <Route path="/share/content/:token" element={<PublicContentReport />} />
      <Route element={<AppShell />}>
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/deals" element={<NotSocialManager><Deals /></NotSocialManager>} />
        <Route path="/pitching" element={<NotSocialManager><Pitching /></NotSocialManager>} />
        <Route path="/templates" element={<NotSocialManager><PitchTemplates /></NotSocialManager>} />
        <Route path="/content-data" element={<NotSocialManager><ContentData /></NotSocialManager>} />
        <Route path="/talent" element={<NotSocialManager><Talent /></NotSocialManager>} />
        <Route path="/talent/:id" element={<NotSocialManager><TalentProfile /></NotSocialManager>} />
        <Route path="/brands" element={<ManagerOnly><Brands /></ManagerOnly>} />
        <Route path="/brands/:id" element={<ManagerOnly><BrandDetail /></ManagerOnly>} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/clients" element={<StrictManagerOnly><Clients /></StrictManagerOnly>} />
        <Route path="/social" element={<SocialAccess><Social /></SocialAccess>} />
        <Route path="/mailers" element={<SocialAccess><Mailers /></SocialAccess>} />
        <Route path="/mailers/:id" element={<SocialAccess><MailerDetail /></SocialAccess>} />
        <Route path="/todos" element={<Todos />} />
        <Route path="/database" element={<SocialAccess><Database /></SocialAccess>} />
        <Route path="/pr-events" element={<NotSocialManager><PrEvents /></NotSocialManager>} />
        <Route path="/projects" element={<SocialAccess><Projects /></SocialAccess>} />
        <Route path="/insights" element={<SocialAccess><Insights /></SocialAccess>} />
        <Route path="/expenses" element={<Expenses />} />
        <Route path="/finances" element={<StrictManagerOnly><Finances /></StrictManagerOnly>} />
        <Route path="/invoicing" element={<NotSocialManager><Invoices /></NotSocialManager>} />
        <Route path="/invoicing/new" element={<ManagerOnly><InvoiceEditor /></ManagerOnly>} />
        <Route path="/invoicing/:id" element={<NotSocialManager><InvoiceEditor /></NotSocialManager>} />
        <Route path="/reports" element={<ManagerOnly><Reports /></ManagerOnly>} />
        <Route path="/settings" element={<ManagerOnly><Settings /></ManagerOnly>} />
      </Route>
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <DataProvider>
            <InvoicesProvider>
              <PitchesProvider>
                <BrandPitchesProvider>
                  <RevenueViewProvider>
                    <EmojiProvider>
                      <AppRouter />
                      <Toaster />
                      <SonnerToaster position="top-center" richColors />
                      <GlobalEmojiInserter />
                    </EmojiProvider>
                  </RevenueViewProvider>
                </BrandPitchesProvider>
              </PitchesProvider>
            </InvoicesProvider>
          </DataProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
